import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Chip, useTheme } from '@mui/material';
import type { Odpoved } from '../api/JsdhNotifikaceService/generated/models/Odpoved';
import type { PravidlaVyjezdu } from '../api/JsdhNotifikaceService/generated/models/DeviceToken';
import { VyjezdSemafor } from './VyjezdSemafor';
import DojezdOdpocet from './DojezdOdpocet';
import { useNestihaVyjezd } from '../hooks/useNestihaVyjezd';
import { tintNestiha } from '../theme/nestihaTint';

interface Vyjezd2026OdpovediSectionProps {
    odpovedi: Odpoved[];
    /** ISO cas vyhlaseni poplachu — bez nej se odpocet dojezdu nevykresli. */
    casVyhlaseni?: string;
    pravidla?: PravidlaVyjezdu;
    showSpecializace?: boolean;
    showFunkce?: boolean;
}

const ADAPTIVE_THRESHOLD = 6;
const MAX_SPEC_VISIBLE = 2;

export const Vyjezd2026OdpovediSection: React.FC<Vyjezd2026OdpovediSectionProps> = ({
    odpovedi, pravidla, showSpecializace = true, showFunkce = true, casVyhlaseni,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const cardTintJedu = isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(46, 125, 50, 0.18)';
    const cardTintNejedu = isDark ? 'rgba(244, 67, 54, 0.22)' : 'rgba(198, 40, 40, 0.18)';
    const jeNestihajici = useNestihaVyjezd(casVyhlaseni, odpovedi);
    const podkladNestiha = tintNestiha(theme, 0.18);

    const { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH, assignedRoleById } = useMemo(() => {
        const jedouOdpovedi = odpovedi.filter(o => o.odpoved === 'JEDU');
        const nejedouOdpovedi = odpovedi.filter(o => o.odpoved === 'NEJEDU');
        // Pravidlo: každá JEDU osoba se započítá právě do jedné kategorie (VD / S / H), shodně se sloupcem JEDU.
        // - Multi-role VD+S: greedy — první obsadí VD slot, další se počítají do S.
        // - VD-only / S-only: počítáme přímo do své role.
        // - H se počítá jen pokud nemá VD ani S (v JSDH má roli "Hasič" baseline většina členů,
        //   takže nezávislé počítání by zdvojovalo velitele/strojníky a součet by neseděl s JEDU).
        let pocetVD = 0, pocetS = 0, pocetH = 0;
        const assignedRoleById = new Map<number, 'VD' | 'S' | 'H'>();
        // Single-role osoby zpracujeme dřív — nemají flexibilitu, takže priorita slotu nehraje roli.
        const sorted = [...jedouOdpovedi].sort(
            (a, b) => (a.funkce?.length ?? 0) - (b.funkce?.length ?? 0),
        );
        for (const o of sorted) {
            if (!o.funkce || o.funkce.length === 0) continue;
            const kody = new Set(o.funkce.map(f => f.kod));
            const isVD = kody.has('VD');
            const isS = kody.has('S');
            const isH = kody.has('H');
            let primary: 'VD' | 'S' | 'H' | null = null;
            if (isVD && isS) {
                if (pocetVD === 0) { pocetVD++; primary = 'VD'; }
                else { pocetS++; primary = 'S'; }
            } else if (isVD) {
                pocetVD++; primary = 'VD';
            } else if (isS) {
                pocetS++; primary = 'S';
            } else if (isH) {
                pocetH++; primary = 'H';
            }
            if (primary) assignedRoleById.set(o.id, primary);
        }
        return { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH, assignedRoleById };
    }, [odpovedi]);

    const useAdaptiveJedu = jedouOdpovedi.length <= ADAPTIVE_THRESHOLD;
    // NEJEDU vždy compact — uživatelům stačí menší font, nepotřebujeme "first 4 large".
    const useAdaptiveNejedu = false;

    const containerRef = useRef<HTMLDivElement>(null);
    // maxItems se počítá zvlášť pro JEDU a NEJEDU — sloupce mohou mít různé velikosti řádků
    // (např. JEDU adaptivní large 60px, NEJEDU compact 40px); jednotný outpočet by undercountnul ten compact.
    const [maxItemsJedu, setMaxItemsJedu] = useState(20);
    const [maxItemsNejedu, setMaxItemsNejedu] = useState(20);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            const h = el.clientHeight;
            // pt: 0, pb: 1 (8px) — pouze spodní padding sekce; nahoře sedí karta těsně.
            const padding = 8;
            // Item height per sloupec podle jejich vlastního adaptive módu — naměřené:
            // compact (1.3rem + py:0.3 + mb:0.5) ≈ 36px; large (1.85rem + py:0.6 + mb:0.5) ≈ 56px.
            const itemHJedu = useAdaptiveJedu ? 56 : 36;
            const itemHNejedu = useAdaptiveNejedu ? 56 : 36;
            const computedJedu = Math.floor((h - padding) / itemHJedu);
            const computedNejedu = Math.floor((h - padding) / itemHNejedu);
            // Vždy zobrazit ideálně min 9 jmen na sloupec; pokud se nevejde, "+X dalších" indikátor.
            const MIN_VISIBLE = 9;
            setMaxItemsJedu(Math.max(MIN_VISIBLE, computedJedu));
            setMaxItemsNejedu(Math.max(MIN_VISIBLE, computedNejedu));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [useAdaptiveJedu, useAdaptiveNejedu]);

    // Pro NEJEDU se nezobrazují funkce ani specializace — tu informaci tam nepotřebujeme.
    // Defenzivní pravidla pro dlouhé hodnoty:
    //   - jméno: truncate s "..." (priorita pro shrinking)
    //   - chips: flexShrink 0 (nemění se), max 2 specializace + "+X" indikátor
    //   - funkce chip: zobrazuje JEN reálně přiřazenou roli (greedy assignment), tooltip má plný seznam
    const renderItem = (o: Odpoved, jedu: boolean, large: boolean) => {
        const nestiha = jeNestihajici(o);
        const barvaPruhu = nestiha ? theme.palette.warning.main : (jedu ? '#2e7d32' : '#c62828');
        const specs = o.specializace ?? [];
        const specsVisible = specs.slice(0, MAX_SPEC_VISIBLE);
        const specsOverflow = Math.max(0, specs.length - specsVisible.length);
        const allFunkceLabel = o.funkce?.map(f => f.kod).join(', ') ?? '';
        const assignedRole = jedu ? assignedRoleById.get(o.id) : undefined;
        const funkceTooltip = allFunkceLabel
            ? (assignedRole && o.funkce && o.funkce.length > 1
                ? `${allFunkceLabel} — započítán jako ${assignedRole}`
                : allFunkceLabel)
            : '';
        return (
            <Box
                key={o.id}
                data-nestiha={nestiha ? 'true' : 'false'}
                sx={{
                    px: 1, py: large ? 0.6 : 0.3, mb: 0.5,
                    display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.5,
                    breakInside: 'avoid', borderRadius: 0.5,
                    borderLeft: '3px solid',
                    borderLeftColor: barvaPruhu,
                    backgroundColor: nestiha ? podkladNestiha : (jedu ? cardTintJedu : cardTintNejedu),
                    minWidth: 0, overflow: 'hidden',
                }}
            >
                <Typography
                    title={o.jmeno}
                    sx={{
                        fontSize: large ? '1.85rem' : '1.3rem',
                        fontWeight: 'bold', minWidth: 0, flex: '1 1 0',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                >
                    {o.jmeno}
                </Typography>
                {/* Specializace (vlevo, hned za jménem) — jen pro JEDU */}
                {jedu && showSpecializace && specsVisible.map(s => (
                    <Chip
                        key={s.id}
                        label={s.kod}
                        title={s.kod}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{
                            height: large ? 28 : 22,
                            fontSize: large ? '0.9rem' : '0.75rem',
                            flexShrink: 0,
                            maxWidth: large ? 90 : 70,
                            '& .MuiChip-label': { px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                    />
                ))}
                {jedu && showSpecializace && specsOverflow > 0 && (
                    <Typography
                        title={specs.map(s => s.kod).join(', ')}
                        sx={{
                            fontSize: large ? '0.9rem' : '0.75rem',
                            fontWeight: 700, color: 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        +{specsOverflow}
                    </Typography>
                )}
                {/* Role a odpocet dojezdu NAD SEBOU na pravem okraji.
                    Vedle sebe zabiraly sirku dvakrat (role 25 px + odpocet 56 px + mezera),
                    a jmeno se pak pri dvou specializacich orezavalo uz kolem 143 px. Ve sloupci
                    zabere dvojice jen sirku toho sirsiho z nich a jmeno tim ziska ~34 px.

                    Role je zamerne PROSTY TEXT, ne Chip: chip ma vlastni vyplen a pozadi, ktere
                    ukusuji vysku navic, a odpocet by se pak musel zmensit ze 17,6 na 13 px. Takhle
                    se dvojice vejde do stavajici vysky radku a odpocet si drzi puvodni velikost
                    (zmereno v prohlizeci: radek zustava 36 px kompaktni / 54 px velky, protoze
                    vysku radku dal urcuje jmeno, ne tenhle sloupec).
                    Ta vyska je podstatna: pocet zobrazenych radku se pocita z konstant nize
                    (itemHJedu / itemHNejedu), takze vyssi radek by tise rozhodil "+ X dalsich". */}
                {((jedu && showFunkce && assignedRole) || (o.dojezdCas && casVyhlaseni)) && (
                    <Box
                        sx={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'flex-end', justifyContent: 'center',
                            flexShrink: 0, ml: 'auto', lineHeight: 1,
                        }}
                    >
                        {jedu && showFunkce && assignedRole && (
                            <Typography
                                title={funkceTooltip}
                                sx={{
                                    fontSize: large ? '0.95rem' : '0.75rem',
                                    fontWeight: 800, lineHeight: 1,
                                    // primary.main je zelena a splyvala by s zelenym podkresem radku JEDU
                                    color: 'info.main',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {assignedRole}
                            </Typography>
                        )}
                        {o.dojezdCas && casVyhlaseni && (
                            <DojezdOdpocet
                                dojezdCas={o.dojezdCas}
                                casVyhlaseni={casVyhlaseni}
                                fontSize={large ? '1.5rem' : '1.1rem'}
                            />
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const jedouVisible = jedouOdpovedi.length > maxItemsJedu
        ? jedouOdpovedi.slice(0, maxItemsJedu - 1)
        : jedouOdpovedi;
    const jedouOverflow = Math.max(0, jedouOdpovedi.length - jedouVisible.length);

    const nejedouVisible = nejedouOdpovedi.length > maxItemsNejedu
        ? nejedouOdpovedi.slice(0, maxItemsNejedu - 1)
        : nejedouOdpovedi;
    const nejedouOverflow = Math.max(0, nejedouOdpovedi.length - nejedouVisible.length);

    return (
        <Paper
            elevation={0}
            square
            sx={{
                flex: 1, p: 0, display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden',
                borderRadius: 0,
            }}
        >
            {/* Vlevo (přitisknutý ke kraji karty): vertikální semafor (JEDU → role → NEJEDU → plomba) */}
            <Box sx={{ flex: '0 0 auto', display: 'flex' }}>
                <VyjezdSemafor
                    orientation="vertical"
                    pocetJedu={jedouOdpovedi.length}
                    pocetNejedu={nejedouOdpovedi.length}
                    pocetVD={pocetVD}
                    pocetS={pocetS}
                    pocetH={pocetH}
                    pravidla={pravidla}
                />
            </Box>
            {/* Vpravo: jména JEDU/NEJEDU ve dvou sloupcích (bez hlaviček — počty jsou v semaforu vlevo).
                pt: 0 — první karta sedí přímo na horní hraně sekce (žádný odstup nahoře). */}
            <Box ref={containerRef} sx={{ flex: 1, pt: 0, px: 1, pb: 1, display: 'flex', gap: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* JEDU sloupec — širší, drží jména s funkcemi a specializacemi */}
                <Box sx={{ flex: 1.6, minWidth: 0 }}>
                    {jedouVisible.map(o => renderItem(o, true, useAdaptiveJedu))}
                    {jedouOverflow > 0 && (
                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', pl: 1 }}>
                            + {jedouOverflow} dalších
                        </Typography>
                    )}
                </Box>
                {/* NEJEDU sloupec — užší (bez chips), drží pouze jména */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {nejedouVisible.map(o => renderItem(o, false, useAdaptiveNejedu))}
                    {nejedouOverflow > 0 && (
                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', pl: 1 }}>
                            + {nejedouOverflow} dalších
                        </Typography>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};
